import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeUnits } from 'Core/hooks';
// Components
import {
  Grid, Container, Card, MenuItem, ListItemIcon, ListItemText, Divider, Menu, Typography
} from "@mui/material";
import { Alert } from '@mui/material';
import { useIntl } from 'react-intl';
import Loading from 'Components/Loading';
import { Button } from 'Components/uiControls';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getUnitsNaturalOrder } from "Core/data/Helpers";
import GlobalAlert from 'Components/GlobalAlert';
//Icons
import DashboardeIcon from '@mui/icons-material/Dashboard'
import AddBoxIcon from '@mui/icons-material/AddBox';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";


//Views
import CreateEditDashboardPanel from './components/gadGets/components/createEdit';
import CreateEditDashboard from './components/createEdit';
import UserGadget from './components/userGadget/index';

import { NotificationManager } from "Components/Notifications";

import {
  openDashboardPanelCreateEditModal,
  searchDashboardPanel,
  updateGadgetDashboardPanel,
  updateGadgetPositionDashboardPanel,
  deleteDashboardPanel,
  openDashboardPanel,
  getPreferences,
  savePreference,
  clearRedux
} from 'Redux/actions';

import './index.scss';

// The dashboard list is backed by a DynamoDB GSI (eventually consistent), so right
// after creating a dashboard a fresh searchDashboards call can momentarily omit it.
// Retry a few times before falling back to dashboard[0] (P3-53905).
const MAX_PREFERENCE_MATCH_RETRIES = 3;
const PREFERENCE_MATCH_RETRY_DELAY_MS = 800;

const DashboardPanel = (props) => {
  const { messages, locale } = useIntl();
  const [columns, setColumns] = useState([]);
  const [dashboardList, setDashboardList] = useState([]);
  const [dashboardSelected, setDashboardSelected] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorDashboard, setAnchorDashboard] = useState(null);
  const [showLoadingDashboard, setShowLoadingDashboard] = useState(true);
  const keyPreferences = 'dashboard';
  const preferencesRequested = useRef(false);
  // True only for a selection the user/create flow made on purpose, as opposed to
  // the default one this component derives from preferences/dashboard[0].
  const isExplicitSelectionRef = useRef(false);
  const preferenceMatchRetriesRef = useRef(0);
  const [editMode, setEditMode] = useState(false);
  const [openConfirmationDelete, setOpenConfirmationDelete] = useState({});
  const dispatch = useDispatch();
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  //Selectors
  const {
    openCreateEdit,
    openCreate,
    dashboard,
    createUserGadget,
    dashboardResponse,
    errorUnitDashboard,
    deleteDashboardResponse
  } = useSelector(state => state.dashboardPanelRedux);

  
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const unitDashboard = useMemo(() => {
    const orderedUnits = getUnitsNaturalOrder(units);
    return orderedUnits.map(unit => ({
      id: unit?.id,
      label: unit?.label
    }));
  }, [units]);


  const { preferencesUser } = useSelector(state => state.preferencesRedux);
  const { authenticatedId } = useSelector(state => state.securityRedux);

  useLayoutEffect(() => {
    dispatch(searchDashboardPanel());

    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (deleteDashboardResponse?.data) {
      setDashboardSelected({});
      dispatch(searchDashboardPanel());
      NotificationManager.success(
        messages["dashboardPanel_edit_confirmation_message_delete_dashboard_description"],
        messages["dashboardPanel_edit_confirmation_message_delete_dashboard_title"],
        4000
      );
      dispatch(clearRedux("DASHBOARD_PANEL", {
        deleteDashboardResponse: null
      }));
    }
  }, [deleteDashboardResponse]);

  useEffect(() => {
    if (errorUnitDashboard) {
      setShowLoadingDashboard(false);
    }
  }, [errorUnitDashboard]);

  useEffect(() => {
    if (!dashboard.length) return;

    // A refreshed list must not override an explicit selection that is still valid.
    // Only an explicit selection skips the recompute below — a default one (picked
    // by this same effect from preferences/dashboard[0]) must still be correctable
    // once preferencesUser arrives, even if it already exists in `dashboard`.
    if (
      isExplicitSelectionRef.current &&
      dashboardSelected?.id &&
      dashboard.some(dash => dash.id === dashboardSelected.id)
    ) {
      return;
    }

    isExplicitSelectionRef.current = false;

    const preferenceKey = preferencesUser?.find(x => x?.preferenceKey === keyPreferences);
    const preferredId = preferenceKey
      ? JSON.parse(preferenceKey.preferenceValue)?.dashboardSelected
      : null;

    const preferredMatch = preferredId ? dashboard.find(dash => dash.id === preferredId) : null;

    // Preference says a specific dashboard should be active, but it's not in this
    // list yet — likely GSI propagation lag right after creation, not a stale
    // preference. Re-fetch instead of immediately falling back to dashboard[0].
    if (preferredId && !preferredMatch && preferenceMatchRetriesRef.current < MAX_PREFERENCE_MATCH_RETRIES) {
      preferenceMatchRetriesRef.current += 1;
      const timeoutId = setTimeout(() => dispatch(searchDashboardPanel()), PREFERENCE_MATCH_RETRY_DELAY_MS);
      return () => clearTimeout(timeoutId);
    }

    preferenceMatchRetriesRef.current = 0;
    const next = preferredMatch || dashboard[0];

    if (next && next.id !== dashboardSelected?.id)
      setDashboardSelected(next);
  }, [preferencesUser, dashboard]);


  useLayoutEffect(() => {
    if (dashboard.length === 0) return;

    setDashboardList(dashboard);

    if (!preferencesRequested.current) {
      preferencesRequested.current = true;
      dispatch(getPreferences({ entityName: "User", entityId: authenticatedId }));
    }
  }, [dashboard]);


  useEffect(() => {
    if (dashboardSelected?.hasOwnProperty('gadgets')) {
      let gadGetsItems = dashboardSelected?.gadgets.slice().sort((a, b) => a.columnOrder > b.columnOrder ? 1 : -1);
      let items = [];
      let columnItems = [];
      gadGetsItems.map(function (gadget) {
        items.push({
          ...gadget
        });
      });
      columnItems.push(items);
      setColumns(columnItems);
    }
  }, [dashboardSelected]);

  const deleteGadget = (gadGet) => {
    let newColumns = columns[0].filter(column => column.id !== gadGet.id)
    setColumns([newColumns]);
    NotificationManager.success(
      messages["dashboardPanel_user_gadget_delete"],
      messages["dashboardPanel_user_gadget_delete_title"],
      4000
    );

  }

  useEffect(() => {
    if (createUserGadget && createUserGadget.hasOwnProperty('id')) {
      let newColumns = columns[0];

      if (newColumns) {
        newColumns.unshift({
          ...createUserGadget,
        });

        setColumns([newColumns]);
        updatePositionGadget([newColumns]);
      }

      dispatch(clearRedux("DASHBOARD_PANEL", {
        createUserGadget: null
      }));

    }
  }, [createUserGadget]);

  useEffect(() => {
    if (dashboardResponse?.createDashboard?.id) {
      const createdDashboard = dashboardResponse.createDashboard;
      // The mutation response already has the full dashboard, so append it to the
      // Redux list directly instead of refetching — that refetch was racing with
      // other in-flight searchDashboards calls and was the source of the selection
      // reverting. Appending to Redux (not just local state) keeps `dashboard` as
      // the single source of truth, so the selection guard above can always find
      // this dashboard in it.
      isExplicitSelectionRef.current = true;
      setDashboardSelected(createdDashboard);
      // Persist it as the user's preference too (same as a manual selection), so the
      // created dashboard is still the one picked after this component unmounts and
      // remounts (e.g. navigating away and back) and preferredId is recomputed.
      dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: keyPreferences, preferenceValue: { dashboardSelected: createdDashboard.id } }));
      dispatch(clearRedux("DASHBOARD_PANEL", {
        dashboard: [...dashboard, createdDashboard],
        dashboardResponse: null
      }));
    }
  }, [dashboardResponse]);


  const onDragEnd = (result, columns, setColumns) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const [removed] = columns[source.droppableId].splice(source.index, 1);
    columns[destination.droppableId].splice(destination.index, 0, removed);
    setColumns(columns);
    updatePositionGadget(columns);
  };

  const updatePositionGadget = (columns) => {
    let gadGets = [];
    columns.map(function (column, columnId) {
      column.map((item, index) => {
        gadGets.push({
          id: item.id,
          dashboardId: item.dashboardId,
          name: item.name,
          type: item.type,
          settings: item.settings,
          columnPanel: item.columnPanel,
          refreshSeconds: item.refreshSeconds,
          columnOrder: index
        });
      });
    });


    dispatch(updateGadgetPositionDashboardPanel({ gadGets }));
  }

  const getData = (gadGet) => {
    dispatch(openDashboardPanelCreateEditModal(false));
    if (gadGet.hasOwnProperty('id')) {
      let newColumns = columns[0].map((newGadget) => {
        if (newGadget.id == gadGet.id) return gadGet
        else return newGadget
      })

      setColumns([newColumns])
    }
  }

  const createEdit = (gadGet) => {
    var gadGetEdit;
    columns.map(function (column, columnId) {
      column.map((item, index) => {
        if (item.id === gadGet.id) {
          gadGetEdit = {
            ...item,
            columnOrder: index
          };

        }
      });
    });
    dispatch(openDashboardPanelCreateEditModal(true, gadGetEdit));
  }

  const expandGadget = (item) => {
    let columnPanel = (item.columnPanel !== 2) ? 2 : 1;
    let newColumns = [];
    columns.map(function (column, columnId) {
      column.map((items, index) => {
        if (items.id === item.id) {
          newColumns.push({
            ...items,
            columnPanel
          })

        } else {
          newColumns.push(items)
        }
      });
    });

    setColumns([newColumns])
    dispatch(updateGadgetDashboardPanel({
      id: item.id,
      dashboardId: item.dashboardId,
      name: item.name,
      type: item.type,
      settings: item.settings,
      columnOrder: item.columnOrder,
      refreshSeconds: item.refreshSeconds,
      columnPanel
    }));
  }

  const renameDashboard = (newName) => {

    setDashboardSelected({
      ...dashboardSelected,
      name: newName,
      gadgets: columns[0]
    })

    let items = [];
    dashboardList.map((dashboardItem) => {
      let value = {
        ...dashboardItem
      };
      if (dashboardItem?.id == dashboardSelected?.id)
        value.name = newName;

      items.push(value);
    });

    setDashboardList(items);
  }

  return (
    <Container maxWidth={false} style={{ height: '92vh', padding: 0, overflow: 'hidden' }}>

      <Grid container direction="row" justifyContent="space-between" alignItems='center' style={{ padding: 15, paddingBottom: 0 }}>
        <Grid
          container
          item
          direction="row"
          xs={12}
          sm={6}
          style={{ zIndex: 2 }}
        >
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Grid item >
              {!editMode && // disable dashboard list when editing
                <div onClick={(event) => setAnchorEl(event.currentTarget)} className='select_dashboard_icon'>
                  <DashboardeIcon fontSize="medium" />

                  {
                    anchorEl &&
                    <KeyboardArrowUpIcon fontSize="small" />
                  }

                  {
                    !anchorEl &&
                    <KeyboardArrowDownIcon fontSize="small" />
                  }

                </div>
              }

              <Menu
                elevation={0}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                className="menu-user"
                //getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {
                  dashboardList?.map((dashboards, index) => (
                    <MenuItem
                      onClick={(c) => {
                        setAnchorEl(null)
                        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: keyPreferences, preferenceValue: { dashboardSelected: dashboards.id } }))
                        isExplicitSelectionRef.current = true;
                        setDashboardSelected(dashboards);
                        dispatch(searchDashboardPanel());

                      }}
                      key={index}>
                      <ListItemIcon>
                        <DashboardeIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={dashboards.name} className={`listMenuItem`} />
                    </MenuItem>
                  ))
                }
                <Divider />
                <MenuItem
                  onClick={(c) => {
                    setAnchorEl(null)
                    dispatch(openDashboardPanel(true));
                  }}
                  key={12}
                >
                  <ListItemIcon>
                    <AddBoxIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={messages['dashboardPanel_title_new']} className={`listMenuItem`} />
                </MenuItem>
              </Menu>

            </Grid>

            <Typography style={{ fontSize: 20, fontWeight: 'bold', marginRight: 20 }} >
              {dashboardSelected?.name}
            </Typography>

            {editMode &&
              <React.Fragment>
                <Button
                  type="icon"
                  icon="more_vert"
                  placement="bottom"
                  onClick={(event) => setAnchorDashboard(event.currentTarget)}
                />
                <Grid item>
                  <Button
                    type="icon"
                    icon="add_box"
                    placement="bottom"
                    tooltip={messages['dashboardPanel_add_gadget']}
                    onClick={(e) => {
                      if (e) e?.preventDefault();
                      dispatch(openDashboardPanelCreateEditModal(true));
                    }}
                  />
                </Grid>
              </React.Fragment>
            }

            <Menu
              elevation={0}
              anchorEl={anchorDashboard}
              open={Boolean(anchorDashboard)}
              onClose={() => setAnchorDashboard(null)}
              className="menu-user"
              //getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
            >
              <MenuItem
                onClick={(c) => {
                  dispatch(openDashboardPanel(true, dashboardSelected));
                  setAnchorDashboard(null);
                }}
                key={1}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>

                <ListItemText primary={messages['dashboardPanel_rename']} className={`listMenuItem`} />
              </MenuItem>
              {dashboard.length > 1 &&
                <MenuItem
                  onClick={(c) => {
                    setOpenConfirmationDelete({
                      active: true,
                      item: dashboardSelected?.id
                    })
                  }}
                  key={2}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={messages['dashboardPanel_delete']} className={`listMenuItem`} />
                </MenuItem>
              }
            </Menu>
          </Grid>
        </Grid>

        <Grid item container xs={12} sm={6} className="text-right" alignItems="center" >
          <Grid container direction="row" justifyContent="flex-end" alignItems="flex-start" style={{ textAlign: 'right' }} >
            <Grid item >
              {!editMode &&
                <Button
                  onClick={(e) => {
                    setEditMode(true);
                  }}
                  className="btn-blue dashboard-edit-btn"
                >
                  {messages['edit']}
                </Button>
              }

              {editMode &&
                <Button
                  onClick={(e) => {
                    setEditMode(false);
                    NotificationManager.success(
                      messages["dashboardPanel_edit_confirmation_message_save_dashboard_description"],
                      messages["dashboardPanel_edit_confirmation_message_save_dashboard_title"],
                      4000
                    );
                  }}
                  className="btn-blue dashboard-edit-btn"
                >
                  {messages['save']}
                </Button>
              }

            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {editMode &&
        <Alert severity="info">{messages['dashboardPanel_edit_info']}</Alert>
      }
      <Grid container direction="row" justifyContent="space-between" alignItems="flex-start" style={{ height: 'calc(100% + 1000px)', overflow: 'hidden' }}>

        <DragDropContext
          onDragEnd={result => onDragEnd(result, columns, setColumns)}
        >
          {!dashboardSelected?.name && <Loading full show={showLoadingDashboard} style={{ zIndex: 9 }} />}
          {columns.map(function (column, columnId) {
            return (
              <Grid
                item
                style={{ height: 'calc(100% + 1000px)', overflow: 'hidden' }}
                sm={12}
                xs={12}
                key={columnId}
              >
                <Droppable droppableId={`${columnId}`} key={columnId}
                  style={{
                    height: '100%'
                  }}
                >
                  {(provided, snapshot) => {
                    return (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                          background: snapshot.isDraggingOver ? "lightblue" : "white",
                          padding: 5,
                          width: 'calc(100% - 10px)',
                          height: windowDimensions.height - 180,
                          overflow: 'auto'
                        }}
                      >
                        {column.map((item, index) => {
                          return (
                            <Draggable
                              key={item.id}
                              draggableId={`drag${item.id}`}
                              index={index}
                              isDragDisabled={!editMode}
                              style={{
                                width: 'calc(50% - 10px)',
                              }}
                            >
                              {(provided, snapshot) => {
                                return (
                                  <Card
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="dashboard_card"
                                    style={{
                                      margin: "10px",
                                      ...provided.draggableProps.style,
                                      transition: `all 100ms ease-out`,
                                      width: item.columnPanel == 2 ? 'calc(100% - 20px)' : 'calc(50% - 20px)',
                                      resize: 'none'
                                    }}
                                  >
                                    <UserGadget item={item} createEdit={createEdit} editMode={editMode} expandGadget={expandGadget} deleteGadget={deleteGadget} />
                                  </Card>

                                );
                              }}

                            </Draggable>

                          );
                        })}
                        {provided.placeholder}
                      </div>
                    );
                  }}

                </Droppable>
              </Grid>
            );
          })}
        </DragDropContext>
      </Grid>
      {
        openCreateEdit && <CreateEditDashboardPanel callback={getData} DashboardId={dashboardSelected?.id} />
      }

      {
        openCreate && <CreateEditDashboard callback={renameDashboard} />
      }

      <GlobalAlert
        open={openConfirmationDelete?.active === true}
        title={`${messages['dashboardPanel_deleteTitle']}`}
        contentText={`${messages['dashboardPanel_delete_dashboard']}`}
        confirmText={messages['dashboardPanel_deleteTitle']}
        type="success"
        cancelText={messages['dashboardPanel_cancelMessage']}
        onClose={() => {
          setOpenConfirmationDelete({})
        }}
        onClick={() => {
          setAnchorDashboard(null);
          dispatch(deleteDashboardPanel(openConfirmationDelete?.item));
          setOpenConfirmationDelete({})
        }}
      />
    </Container>

  );
}

export default DashboardPanel;
